import React from "react";
import {
    AppBar,
    Toolbar,
    Typography,
    Box,
    ThemeProvider,
    createTheme,
    CssBaseline,
    IconButton,
    Tooltip,
    useMediaQuery,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
} from "@mui/material";
import { Info, Code, QrCode, Assistant } from "@mui/icons-material";
import { AppLogo } from "../components/AppLogo";
import { MobileNav } from "../components/MobileNav";

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        primary: {
            main: "#3B82F6", // Vibrant Blue
            light: "#60A5FA",
            dark: "#2563EB",
        },
        secondary: {
            main: "#10B981", // Emerald Green
            light: "#34D399",
            dark: "#059669",
        },
        background: {
            default: "#000000", // True black for OLED feel
            paper: "#111111",
        },
        text: {
            primary: "#F3F4F6",
            secondary: "#9CA3AF",
        },
    },
    typography: {
        fontFamily: "'Outfit', sans-serif",
        h6: {
            fontWeight: 600,
            letterSpacing: "-0.025em",
        },
        button: {
            fontWeight: 600,
            textTransform: "none",
            letterSpacing: "0.025em",
        },
    },
    shape: {
        borderRadius: 12,
    },
    components: {
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    backgroundColor: "rgba(17, 17, 17, 0.7)",
                    backdropFilter: "blur(20px)",
                    border: "1px solid rgba(255, 255, 255, 0.08)",
                    boxShadow: "0 4px 30px rgba(0, 0, 0, 0.5)",
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundImage: "none",
                    backgroundColor: "rgba(0, 0, 0, 0.6)",
                    backdropFilter: "blur(20px)",
                    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
                    boxShadow: "none",
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& .MuiOutlinedInput-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.03)",
                        transition: "all 0.2s ease-in-out",
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 0.06)",
                        },
                        "&.Mui-focused": {
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            boxShadow: "0 0 0 2px rgba(59, 130, 246, 0.5)",
                        },
                        "& fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.1)",
                        },
                        "&:hover fieldset": {
                            borderColor: "rgba(255, 255, 255, 0.2)",
                        },
                        "&.Mui-focused fieldset": {
                            borderColor: "#3B82F6",
                            borderWidth: 1,
                        },
                    },
                },
            },
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    padding: "8px 16px",
                    boxShadow: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                        transform: "translateY(-1px)",
                        boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)",
                    },
                    "&:active": {
                        transform: "translateY(0)",
                    },
                },
                containedPrimary: {
                    background: "linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    fontWeight: 500,
                },
                filledPrimary: {
                    background: "rgba(59, 130, 246, 0.2)",
                    color: "#60A5FA",
                    border: "1px solid rgba(59, 130, 246, 0.3)",
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    transition: "all 0.2s",
                    minHeight: 48,
                    "&.Mui-selected": {
                        color: "#60A5FA",
                        textShadow: "0 0 20px rgba(59, 130, 246, 0.5)",
                    },
                },
            },
        },
    },
});

interface MainLayoutProps {
    children: React.ReactNode;
    onInfoClick: () => void;
    currentTab?: number;
    onTabChange?: (event: React.SyntheticEvent, newValue: number) => void;
}

export const MainLayout: React.FC<MainLayoutProps> = ({ children, onInfoClick, currentTab, onTabChange }) => {
    const theme = darkTheme;
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Box sx={{
                position: "relative",
                height: "100vh",
                width: "100vw",
                overflow: "hidden",
                background: "#000"
            }}>
                {/* Map Background Container (Children will be the map) */}
                <Box sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 0
                }}>
                    {children}
                </Box>

                {/* Floating Header (Logo & Search Area) */}
                <Box sx={{
                    position: "absolute",
                    top: 20,
                    left: isMobile ? 20 : 440, // Offset for panel on desktop
                    right: 20,
                    zIndex: 1000,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    pointerEvents: "none", // Let clicks pass through to map where empty
                }}>
                    {/* Logo Area (Only visible if not covered by panel or on mobile) */}
                    <Box sx={{
                        pointerEvents: "auto",
                        display: isMobile ? "flex" : "none", // Hidden on desktop as it's in the panel
                        alignItems: "center",
                        gap: 1.5,
                        background: "rgba(17, 17, 17, 0.85)",
                        backdropFilter: "blur(20px)",
                        p: 1,
                        px: 2,
                        borderRadius: "16px",
                        border: "1px solid rgba(255, 255, 255, 0.1)",
                        boxShadow: "0 4px 20px rgba(0,0,0,0.3)"
                    }}>
                        <AppLogo />
                        <Typography variant="h6" component="h1" sx={{ fontWeight: 700, fontSize: "1rem" }}>DIGIPIN</Typography>
                    </Box>

                    {/* Right Side Actions - Info moved to sidebar */}
                    <Box sx={{ pointerEvents: "auto", display: "flex", gap: 1, ml: "auto" }}>
                        {/* Info button removed from here */}
                    </Box>
                </Box>

                {/* Desktop Navigation Sidebar */}
                {!isMobile && currentTab !== undefined && onTabChange && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            bottom: 0,
                            left: 0,
                            width: "80px", // Fixed width sidebar
                            zIndex: 1100, // Higher than floating panel
                            background: "rgba(10, 10, 10, 0.8)", // Deep dark
                            backdropFilter: "blur(20px)",
                            borderRight: "1px solid rgba(255, 255, 255, 0.08)",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 4,
                            py: 4,
                            boxShadow: "4px 0 24px rgba(0,0,0,0.5)"
                        }}
                    >
                        {/* Logo / Brand Icon at Top (Optional, but looks pro) */}
                        <Box sx={{ position: "absolute", top: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                            {/* Small Logo or Brand Mark could go here */}
                        </Box>

                        <List sx={{
                            p: 0,
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 3, // Spacing between icons
                            alignItems: "center"
                        }}>
                            <ListItem disablePadding sx={{ display: "block", width: "auto" }}>
                                <Tooltip title="Encode Location" placement="right" arrow>
                                    <ListItemButton
                                        selected={currentTab === 0}
                                        onClick={(e) => onTabChange(e, 0)}
                                        sx={{
                                            minHeight: 56,
                                            width: 56,
                                            borderRadius: "16px",
                                            justifyContent: "center",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            backgroundColor: currentTab === 0 ? "rgba(59, 130, 246, 0.15)" : "transparent",
                                            border: currentTab === 0 ? "1px solid rgba(59, 130, 246, 0.3)" : "1px solid transparent",
                                            "&:hover": {
                                                backgroundColor: "rgba(59, 130, 246, 0.1)",
                                                transform: "scale(1.05)",
                                                border: "1px solid rgba(59, 130, 246, 0.2)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(59, 130, 246, 0.2)",
                                                boxShadow: "0 0 15px rgba(59, 130, 246, 0.3)",
                                                "&:hover": {
                                                    backgroundColor: "rgba(59, 130, 246, 0.25)",
                                                }
                                            }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: 0,
                                                justifyContent: "center",
                                                color: currentTab === 0 ? "#60A5FA" : "#9CA3AF"
                                            }}
                                        >
                                            <Code sx={{ fontSize: 28 }} />
                                        </ListItemIcon>
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>
                            <ListItem disablePadding sx={{ display: "block", width: "auto" }}>
                                <Tooltip title="Decode DIGIPIN" placement="right" arrow>
                                    <ListItemButton
                                        selected={currentTab === 1}
                                        onClick={(e) => onTabChange(e, 1)}
                                        sx={{
                                            minHeight: 56,
                                            width: 56,
                                            borderRadius: "16px",
                                            justifyContent: "center",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            backgroundColor: currentTab === 1 ? "rgba(16, 185, 129, 0.15)" : "transparent",
                                            border: currentTab === 1 ? "1px solid rgba(16, 185, 129, 0.3)" : "1px solid transparent",
                                            "&:hover": {
                                                backgroundColor: "rgba(16, 185, 129, 0.1)",
                                                transform: "scale(1.05)",
                                                border: "1px solid rgba(16, 185, 129, 0.2)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(16, 185, 129, 0.2)",
                                                boxShadow: "0 0 15px rgba(16, 185, 129, 0.3)",
                                                "& .MuiListItemIcon-root": { color: "#34D399" },
                                                "&:hover": {
                                                    backgroundColor: "rgba(16, 185, 129, 0.25)",
                                                }
                                            }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: 0,
                                                justifyContent: "center",
                                                color: currentTab === 1 ? "#34D399" : "#9CA3AF"
                                            }}
                                        >
                                            <QrCode sx={{ fontSize: 28 }} />
                                        </ListItemIcon>
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>
                            <ListItem disablePadding sx={{ display: "block", width: "auto" }}>
                                <Tooltip title="AI Assistant" placement="right" arrow>
                                    <ListItemButton
                                        selected={currentTab === 2}
                                        onClick={(e) => onTabChange(e, 2)}
                                        sx={{
                                            minHeight: 56,
                                            width: 56,
                                            borderRadius: "16px",
                                            justifyContent: "center",
                                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                            backgroundColor: currentTab === 2 ? "rgba(236, 72, 153, 0.15)" : "transparent",
                                            border: currentTab === 2 ? "1px solid rgba(236, 72, 153, 0.3)" : "1px solid transparent",
                                            "&:hover": {
                                                backgroundColor: "rgba(236, 72, 153, 0.1)",
                                                transform: "scale(1.05)",
                                                border: "1px solid rgba(236, 72, 153, 0.2)",
                                            },
                                            "&.Mui-selected": {
                                                backgroundColor: "rgba(236, 72, 153, 0.2)",
                                                boxShadow: "0 0 15px rgba(236, 72, 153, 0.3)",
                                                "& .MuiListItemIcon-root": { color: "#F472B6" },
                                                "&:hover": {
                                                    backgroundColor: "rgba(236, 72, 153, 0.25)",
                                                }
                                            }
                                        }}
                                    >
                                        <ListItemIcon
                                            sx={{
                                                minWidth: 0,
                                                mr: 0,
                                                justifyContent: "center",
                                                color: currentTab === 2 ? "#F472B6" : "#9CA3AF"
                                            }}
                                        >
                                            <Assistant sx={{ fontSize: 28 }} />
                                        </ListItemIcon>
                                    </ListItemButton>
                                </Tooltip>
                            </ListItem>
                        </List>

                        {/* Bottom Action / Info */}
                        <Box sx={{ position: "absolute", bottom: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                            <Tooltip title="About" placement="right">
                                <IconButton
                                    onClick={onInfoClick}
                                    sx={{
                                        color: "text.secondary",
                                        "&:hover": { color: "text.primary", background: "rgba(255,255,255,0.05)" }
                                    }}
                                >
                                    <Info />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                )}

                {/* Mobile Navigation */}
                {isMobile && currentTab !== undefined && onTabChange && (
                    <MobileNav value={currentTab} onChange={onTabChange} />
                )}
            </Box>
        </ThemeProvider>
    );
};
